/**
 * Domain enumerations.
 *
 * These arrays are the single source of truth. The Drizzle schema builds its
 * Postgres enums from them and the application derives its TypeScript unions
 * from them, so a value added here cannot be forgotten in one of the two
 * places. Order matters: Postgres enum values are ordered, and reordering an
 * existing enum is a migration, not an edit.
 */

/**
 * What a user may act as, beyond the Player role every account has.
 *
 * `developer` was here and went with the studios. It named somebody acting for
 * an organisation whose games we sold, and there is no such organisation: the
 * catalogue is ours, and the person who edits it holds `admin`.
 */
export const USER_ROLES = ['player', 'creator', 'admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Section 10. Product types the storefront sells. */
export const PRODUCT_TYPES = [
  'base_game',
  'dlc',
  'bundle',
  'demo',
  'soundtrack',
  'playtest_build',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

/** Where a product sits in its lifecycle. Developer-controlled, per section 10.1. */
export const RELEASE_STATES = ['draft', 'coming_soon', 'released', 'delisted'] as const;
export type ReleaseState = (typeof RELEASE_STATES)[number];

/**
 * Section 5.5. Every title is manually reviewed before listing, so curation
 * state is tracked separately from release state: a developer controls the
 * latter and only GOODBUILD moves the former.
 */
export const CURATION_STATES = ['draft', 'submitted', 'in_review', 'approved', 'rejected'] as const;
export type CurationState = (typeof CURATION_STATES)[number];

/**
 * Section 18. How an entitlement was acquired. Every one of these is a
 * GOODBUILD entitlement; there is no third-party key source and there must
 * never be one (hard rule 16).
 */
export const ENTITLEMENT_SOURCES = [
  'purchase',
  'key',
  'playtest',
  'gift',
  'bundle',
  'first_hundred',
  'admin_gift',
] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[number];

/**
 * Why an entitlement was revoked. A game is never revoked as a penalty
 * (hard rule 19), so there is no value here for a missed deadline or any
 * other disciplinary outcome, and there must never be one.
 */
export const ENTITLEMENT_REVOCATION_REASONS = [
  'refund',
  'chargeback',
  'playtest_wave_closed',
  'gift_reclaimed_unclaimed',
  'fraud',
  'granted_in_error',
] as const;
export type EntitlementRevocationReason = (typeof ENTITLEMENT_REVOCATION_REASONS)[number];

export const ORDER_STATES = [
  'pending',
  'cleared',
  'partially_refunded',
  'refunded',
  'failed',
  'cancelled',
] as const;
export type OrderState = (typeof ORDER_STATES)[number];

/** Section 6. Lifecycle of a commission or split entry. */
export const PAYABLE_STATES = ['pending', 'cleared', 'paid', 'reversed', 'clawed_back'] as const;
export type PayableState = (typeof PAYABLE_STATES)[number];

/**
 * Section 12. Attribution paths, declared in priority order: the earlier a
 * value appears, the stronger the signal. Explicit code entry at checkout
 * always wins.
 *
 * The order of this array is load-bearing. `attributionPriority` derives the
 * ranking from it, so reordering these values changes who gets paid.
 */
export const ATTRIBUTION_PATHS = [
  /** Explicit code entry at checkout. Strongest signal, always wins. */
  'code_entry',
  /** Purchase from a creator page, bound to the session at navigation. */
  'creator_page',
  /** goodbuild.cc/g/<slug>?c=<code>. Cookie plus server-side session record. */
  'creator_link',
  /**
   * Buyer follows exactly one creator who has shelved this game, and no
   * stronger signal exists. Most abuse-prone, so it ships behind a flag and is
   * optional per developer.
   */
  'follow',
  /** Wishlisted from a creator page, credited on eventual purchase in window. */
  'wishlist',
] as const;
export type AttributionPath = (typeof ATTRIBUTION_PATHS)[number];

/** Lower is stronger. Derived from declaration order so the two cannot drift. */
export function attributionPriority(path: AttributionPath): number {
  return ATTRIBUTION_PATHS.indexOf(path);
}

/** Section 9. Channels a creator can connect and have verified. */
export const CHANNEL_PLATFORMS = ['twitch', 'youtube', 'tiktok', 'kick'] as const;
export type ChannelPlatform = (typeof CHANNEL_PLATFORMS)[number];

/**
 * The platforms a creator can *prove* they own, which is not all of them.
 *
 * #91 makes ownership of a channel the first thing the creator application
 * establishes, and proof is an OAuth round trip through the platform's own API.
 * That is only possible where the platform offers one to us, so this is a
 * subset of `CHANNEL_PLATFORMS` rather than a second list of the same thing:
 * detection (`CONTENT_PLATFORMS`) still finds coverage on all four, and
 * `channel_platform` columns still accept all four, because a channel that was
 * verified some other way is a state this list must not make unrepresentable.
 *
 * **Kick is deliberately absent** (#92). It has no public OAuth application
 * process open to us, so offering it would be a card that begins a round trip
 * and returns an error from somebody else's site — the worst of the three
 * possible states, because it looks like our fault and happens after somebody
 * has left. The same argument `socialProviders` makes about a provider with no
 * client id, one layer up.
 *
 * The order is the order the cards are drawn in, and it is by audience size on
 * this platform rather than alphabetical.
 */
export const VERIFIABLE_PLATFORMS = ['twitch', 'youtube', 'tiktok'] as const;
export type VerifiablePlatform = (typeof VERIFIABLE_PLATFORMS)[number];

export function isVerifiablePlatform(value: unknown): value is VerifiablePlatform {
  return typeof value === 'string' && (VERIFIABLE_PLATFORMS as readonly string[]).includes(value);
}

/**
 * The providers a desktop launcher may start a sign-in with.
 *
 * Here rather than in `lib/launcher-sign-in.ts`, where it was, because two
 * things read it and one of them is the schema: `launcher_sign_in.provider`
 * carries the same list as a table-level CHECK, and SQLite cannot alter one —
 * so the two drifting apart is not a lint failure, it is a migration. A schema
 * file reaching into `lib/` to avoid that would be a layering inversion, and it
 * was: it pulled the whole sign-in module, `crypto.subtle` and all, into the
 * browser app's typecheck.
 *
 * **It has to hold every provider the browser offers.** Somebody who created
 * their account with Google has no password to fall back on, so a launcher
 * accepting a subset would be a desktop application those people cannot open,
 * refusing them with a constraint name.
 *
 * **TikTok is not one of them, and is not an oversight.** It is a platform a
 * creator proves a channel on — `VERIFIABLE_PLATFORMS` has it — and deliberately
 * not a way into an account: Login Kit hands out no email address at all, so an
 * account created with it would have nothing in `user.email` and no way back in
 * except TikTok. The credentials are configured on a deployment either way,
 * which is exactly why this list is written out rather than derived from what
 * is configured.
 *
 * Fixed rather than derived from whatever is configured, because the value is
 * interpolated into a URL the *system browser* is sent to — this is the check
 * that says it is a word we chose rather than one a caller did. Whether a
 * provider is configured is asked separately, in `launcher-oauth.ts`.
 */
export const LAUNCHER_PROVIDERS = ['discord', 'twitch', 'google'] as const;
export type LauncherProvider = (typeof LAUNCHER_PROVIDERS)[number];

/** Section 14. A creator reaches accredited state before campaigns open up. */
export const CREATOR_STATES = ['applied', 'approved', 'accredited', 'suspended'] as const;
export type CreatorState = (typeof CREATOR_STATES)[number];

export const PAYOUT_STATES = ['draft', 'processing', 'paid', 'failed'] as const;
export type PayoutState = (typeof PAYOUT_STATES)[number];

/** Section 4.3. Community media types. */
export const MEDIA_KINDS = ['screenshot', 'clip'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

/**
 * Where a piece of media came from.
 *
 * Launcher captures carry playtime, build version and hardware automatically,
 * which is what makes them verifiable in a way an uploaded file is not.
 */
export const MEDIA_SOURCES = ['launcher', 'web'] as const;
export type MediaSource = (typeof MEDIA_SOURCES)[number];

/**
 * Section 4.3. Nothing is publicly visible before automated screening.
 *
 * `removed_developer_request` is distinct from `rejected` on purpose: a
 * developer flag leaves a visible marker with a reason category, and cannot
 * silently remove anything.
 */
export const MODERATION_STATES = [
  'pending',
  'approved',
  'rejected',
  'removed_developer_request',
] as const;
export type ModerationState = (typeof MODERATION_STATES)[number];

/** Why a developer flagged a media item. Shown on the removal marker. */
export const MEDIA_FLAG_REASONS = ['spoiler', 'fixed_bug', 'eula_violation', 'other'] as const;
export type MediaFlagReason = (typeof MEDIA_FLAG_REASONS)[number];

/** A review is positive or negative. There is no star rating. */
export const REVIEW_SENTIMENTS = ['positive', 'negative'] as const;
export type ReviewSentiment = (typeof REVIEW_SENTIMENTS)[number];

/**
 * Section 5.6. Why a review carries a disclosure label.
 *
 * `first_hundred_free_copy` is permanent, survives edits, and cannot be
 * removed by the reviewer or the developer.
 */
export const REVIEW_DISCLOSURES = [
  'first_hundred_free_copy',
  'campaign_key',
  'developer',
  'free_copy_from_goodbuild',
] as const;
export type ReviewDisclosure = (typeof REVIEW_DISCLOSURES)[number];

/**
 * Section 5.5. The life of a First Hundred claim.
 *
 * `failed` is permanent and global: one strike, across every title, forever.
 * `voided` is the carve-out for the platform or the developer failing, and
 * carries no penalty. There is deliberately no state for "revoked", because
 * the game is never taken back.
 */
export const FIRST_HUNDRED_CLAIM_STATES = ['claimed', 'fulfilled', 'failed', 'voided'] as const;
export type FirstHundredClaimState = (typeof FIRST_HUNDRED_CLAIM_STATES)[number];

/** Section 5.3. A slot held for a friend. */
export const RESERVATION_STATES = ['held', 'accepted', 'expired', 'cancelled'] as const;
export type ReservationState = (typeof RESERVATION_STATES)[number];

/** Section 4.9. Why a claim was voided, so the carve-out is auditable. */
export const VOID_REASONS = [
  'broken_build',
  'unlaunchable',
  'delisted_mid_window',
  'server_outage',
  'platform_outage',
] as const;
export type VoidReason = (typeof VOID_REASONS)[number];

/** Section 4.9. The structured declaration a relaunch must carry. */
export const UPDATE_TYPES = [
  'new_content',
  'new_mode',
  'systemic_rework',
  'performance_or_platform',
] as const;
export type UpdateType = (typeof UPDATE_TYPES)[number];

export const RELAUNCH_STATES = ['draft', 'pending_review', 'approved', 'rejected'] as const;
export type RelaunchState = (typeof RELAUNCH_STATES)[number];

/** Section 14. Discoverable campaigns are browsable; hidden ones are invite only. */
export const CAMPAIGN_VISIBILITIES = ['discoverable', 'hidden'] as const;
export type CampaignVisibility = (typeof CAMPAIGN_VISIBILITIES)[number];

export const CAMPAIGN_APPLICATION_STATES = [
  'applied',
  'approved',
  'declined',
  'withdrawn',
] as const;
export type CampaignApplicationState = (typeof CAMPAIGN_APPLICATION_STATES)[number];

/**
 * Section 14. The life of a code in the code manager.
 *
 * `recycled` exists because unredeemed codes go back into the pool rather than
 * being lost, which is table stakes for this category of tool.
 */
export const CAMPAIGN_CODE_STATES = [
  'available',
  'assigned',
  'delivered',
  'redeemed',
  'recycled',
] as const;
export type CampaignCodeState = (typeof CAMPAIGN_CODE_STATES)[number];

/**
 * Section 15. A playtest participant.
 *
 * `revoked` is reachable and load-bearing: wave close revokes access, which is
 * exactly why builds are GOODBUILD entitlements and never third-party keys.
 */
export const PLAYTEST_PARTICIPANT_STATES = [
  'applied',
  'invited',
  'accepted',
  'declined',
  'revoked',
] as const;
export type PlaytestParticipantState = (typeof PLAYTEST_PARTICIPANT_STATES)[number];

/** Section 7.2. Where a piece of detected coverage was found. */
export const CONTENT_PLATFORMS = ['twitch', 'youtube', 'tiktok', 'kick'] as const;
export type ContentPlatform = (typeof CONTENT_PLATFORMS)[number];

/** Section 7.2. How a match was made, which drives the confidence score. */
export const MATCH_METHODS = [
  /** The platform's own game tag. Strongest and cheapest. */
  'platform_game_tag',
  /** Title text matched the product name. */
  'title_match',
  /** The channel has covered this title before. */
  'channel_history',
  /** A creator submitted the link themselves. Instant, free and reliable. */
  'self_reported',
] as const;
export type MatchMethod = (typeof MATCH_METHODS)[number];

/** Section 7. Who a social link belongs to. */
export const SOCIAL_OWNER_TYPES = ['user'] as const;
export type SocialOwnerType = (typeof SOCIAL_OWNER_TYPES)[number];

/**
 * Who wrote the description on a store page.
 *
 * The page says which of these it is, and the label is derived from this value
 * rather than stored beside it, so a description cannot be shown as something it
 * is not.
 *
 * **`generated` was here and is gone.** It meant *auto-generated from players'
 * reviews*, and it was the store page being written by its own audience. The
 * store page is a store page now: somebody who made the game wrote the words, or
 * they came off that game’s Steam page. Reviews are still collected and still
 * shown as reviews; they no longer describe the product.
 */
/**
 * The other shops a game is sold in.
 *
 * **Not `CONTENT_PLATFORMS`, and the two must not be merged.** That list is
 * Twitch and YouTube — where a creator posts about a game — and this is where a
 * player can buy one. They answer different questions and they will drift apart
 * the first time either grows.
 *
 * Three because three is what the store page has links for. Adding a fourth is
 * this array, a host in `store-links.ts`, and a CHECK in a migration; nothing
 * else reads the set, and the store page renders whatever is in it.
 */
export const STORE_PLATFORMS = ['steam', 'epic', 'itch'] as const;
export type StorePlatform = (typeof STORE_PLATFORMS)[number];

export const DESCRIPTION_SOURCES = [
  /** Written or accepted by staff. It is ours, and it has an author. */
  'developer',
  /**
   * Imported from the game’s Steam page.
   *
   * Nobody here wrote it, so it carries no author — the studio did, for Valve.
   * `steam_app_id` records which page, which is the whole of the provenance.
   */
  'steam',
] as const;
export type DescriptionSource = (typeof DESCRIPTION_SOURCES)[number];

export const REFUND_REQUEST_STATES = ['pending', 'approved', 'rejected'] as const;
export type RefundRequestState = (typeof REFUND_REQUEST_STATES)[number];

export const LEDGER_ACCOUNT_KINDS = ['asset', 'liability', 'revenue', 'expense'] as const;
export type LedgerAccountKind = (typeof LEDGER_ACCOUNT_KINDS)[number];

export const LEDGER_ACCOUNT_OWNER_TYPES = ['platform', 'user', 'creator', 'developer'] as const;
export type LedgerAccountOwnerType = (typeof LEDGER_ACCOUNT_OWNER_TYPES)[number];

export const LEDGER_DIRECTIONS = ['debit', 'credit'] as const;
export type LedgerDirection = (typeof LEDGER_DIRECTIONS)[number];

/**
 * The documents somebody can be asked to accept.
 *
 * One kind per audience and obligation, versioned separately: the buyer-facing
 * terms change on a different schedule from the privacy policy, and a creator
 * should not be asked to re-accept a document that did not change.
 *
 * **Two kinds were here and went with the studios.** `developer_terms` was what
 * a studio agreed to in order to list a game, and `publishing_agreement` was
 * the per-title contract Firma had signed. Both named an obligation between
 * GOODBUILD and somebody else's company, and there is no such company: the
 * catalogue is ours. `0034_drop_studios` deletes the rows as well as the kinds,
 * because a `legal_document` nobody can accept is not evidence of anything.
 */
export const LEGAL_DOCUMENT_KINDS = [
  /** What a creator agrees to in order to earn commission. */
  'creator_terms',
  /** The terms for using the site at all, and for buying on it. */
  'platform_terms',
  'privacy_policy',
  'refund_policy',
  /** GDPR Article 28, for anyone we process personal data on behalf of. */
  'data_processing',
] as const;
export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number];

/**
 * What kind of legal person is contracting with GOODBUILD.
 *
 * Drives which identifiers are required — an individual has no company number.
 * Read by `legal_entity`, whose only remaining party is a creator.
 */
export const LEGAL_ENTITY_TYPES = ['company', 'sole_trader', 'individual'] as const;
export type LegalEntityType = (typeof LEGAL_ENTITY_TYPES)[number];

/**
 * Who a purchase is being made for.
 *
 * **This is the column that replaces a quantity.** A games purchase cannot
 * carry three of a thing, because an entitlement is held or it is not and
 * `order_line` has said so since the initial schema — *"Quantity is
 * deliberately absent: an entitlement is held or it is not, and buying two of
 * the same game is not a thing."*
 *
 * A quantity column would therefore have exactly one legal value, and the one
 * case that ever wants a second copy is gifting (#74): buying the same game
 * twice is legitimate when the two copies land in different libraries. Modelled
 * as a quantity that would be `2`, something downstream has to invent which of
 * the two copies is whose. Modelled as a recipient, the question does not arise.
 *
 * There is deliberately no third value. Everything bought here ends up in
 * exactly one library — the buyer’s, or one person’s. Anything that is neither
 * (a bundle, a key batch, a campaign allocation) is a different table with
 * different rules, not a recipient nobody can name.
 */
export const PURCHASE_RECIPIENTS = ['self', 'gift'] as const;
export type PurchaseRecipient = (typeof PURCHASE_RECIPIENTS)[number];

/**
 * Which merchant of record a payment went through.
 *
 * **Not a list of vendors so much as a list of answers to "who sold this".**
 * The payment layer in `apps/api/src/lib/payments` exists because that
 * answer has already changed once — Stripe Connect to Stripe Managed Payments —
 * and is changing again, and because the domain must never learn which one is
 * in use. This array is the one place both halves agree on the vocabulary: the
 * `PAYMENT_PROVIDER` variable is checked against it, and the `payment_product`
 * mapping table carries it in a CHECK-constrained column so a row can never
 * name a provider no build knows about.
 *
 * **One entry, and the seam stays anyway.** `stripe` was here and is gone: the
 * adapter, its five modules, the dependency and the webhook route are all
 * deleted, and `0035_creem_only` narrowed the CHECK constraints that used to
 * accept the name. What is deliberately *not* gone is the layer around this —
 * `PaymentProvider`, `paymentProvider()` and the `provider` column on three
 * tables — because the thing that changed is which vendor takes the money, not
 * the fact that the answer changes. It has changed twice now, and the domain
 * has never had to know.
 *
 * That is the same call `lib/payouts/unintegrated.ts` records for the payout
 * rail: a registry with one name in it is cheap, and an abstraction removed on
 * the day it has one implementation is an abstraction rebuilt the next time
 * there are two.
 *
 * A provider added here is a provider the database will accept a mapping row
 * for; it is not a provider that exists until there is an adapter behind it,
 * and `paymentProvider()` names the ones there are when somebody selects one
 * there is not.
 */
export const PAYMENT_PROVIDERS = ['creem'] as const;
export type PaymentProviderName = (typeof PAYMENT_PROVIDERS)[number];

export function isPaymentProviderName(value: unknown): value is PaymentProviderName {
  return typeof value === 'string' && (PAYMENT_PROVIDERS as readonly string[]).includes(value);
}

/**
 * The marketing art a title carries, one entry per surface that shows one.
 *
 * **Every one of these is a Steam size, exactly** (#134). The point is not that
 * Steam's dimensions are especially good — it is that a studio arriving here
 * already has these files, cut to these ratios, approved by whoever approves
 * art, sitting in the folder they submitted to Valve from. Asking them for
 * `1920×620` instead of `3840×1240` turns a drag-and-drop into an afternoon in
 * Photoshop for no benefit anybody can name. `lib/title-art/sizes.ts` holds the
 * numbers and the surface each one is for.
 *
 * The names are ours rather than Valve's where Valve's would confuse. `header`
 * and `capsule_*` keep their Steam names because a studio knows them; `logo` is
 * Steam's "library logo", the transparent one; `page_background` is what Steam
 * calls the page background and generates automatically if you do not supply
 * one.
 *
 * Not every title has every one, and nothing here says it must. A title with no
 * `library_capsule` gets the art-pending plate on a portrait grid rather than a
 * stretched landscape one — see `Capsule` in `apps/app/src/ui`, which has always
 * degraded that way and is the reason this can ship incrementally.
 */
export const TITLE_ART_KINDS = [
  'header',
  'capsule_main',
  'capsule_small',
  'capsule_vertical',
  'library_capsule',
  'library_hero',
  'logo',
  'page_background',
] as const;
export type TitleArtKind = (typeof TITLE_ART_KINDS)[number];

/**
 * How a piece of title art got here.
 *
 * The distinction is disclosure before it is anything else, and it is why this
 * is a column rather than something inferred from whether `source_url` is null.
 * `steam` means we fetched it from Valve's CDN because a studio pointed us at
 * their store page (#130); `upload` means somebody put the file there
 * deliberately (#133). An administrator looking at a title that renders wrong
 * needs to know which, because the fix is different: re-run the import, or ask
 * the studio for the file.
 */
export const TITLE_ART_SOURCES = ['steam', 'upload'] as const;
export type TitleArtSource = (typeof TITLE_ART_SOURCES)[number];

export const TITLE_MEDIA_KINDS = ['trailer', 'screenshot'] as const;
export type TitleMediaKind = (typeof TITLE_MEDIA_KINDS)[number];

export const TITLE_MEDIA_SOURCES = ['steam', 'upload'] as const;
export type TitleMediaSource = (typeof TITLE_MEDIA_SOURCES)[number];

export const MAX_TITLE_MEDIA_PER_TITLE = 24;
